import type { CompletionStatus, SessionView, StepView } from '~/helpers/learner/session-view'
import { completionStatus, courseLaunchHref, isPath, progressPct, steps, stepStatus } from '~/helpers/learner/session-view'
import { matchesQuery, searchWords } from '~/utils/search-text'

/** Un panneau d'en-tête n'est pas une page de résultats : au-delà, on précise. */
const MAX_SESSIONS = 5
const MAX_MODULES = 8

export type SessionHit = {
  session: SessionView
  status: CompletionStatus
  progress: number
  /** Titre du module d'une formation qui n'en a qu'un : c'est par lui qu'on l'a souvent trouvée. */
  moduleTitle?: string
  /** Formation d'un seul module que l'API n'a pas encore ouverte. */
  locked: boolean
  /** Route ouverte quand on choisit ce résultat. */
  target: string
}

export type ModuleHit = {
  session: SessionView
  step: StepView
  status: CompletionStatus
  locked: boolean
  target: string
}

export type LearnerSearchResults = {
  sessions: SessionHit[]
  modules: ModuleHit[]
}

/**
 * Où mène une formation : l'écran d'un parcours ; pour une formation d'un seul
 * module, son lancement direct quand il est ouvert — comme sa carte d'accueil
 * —, sinon son écran, qui dit pourquoi il est fermé.
 */
function sessionTarget(session: SessionView): string {
  return courseLaunchHref(session) ?? `/my-sessions/${session.id}`
}

/**
 * Recherche dans les formations de l'apprenant (#109), sur `mySessions` déjà
 * chargé : nom des formations, titre des modules.
 *
 * Une formation d'un seul module se trouve aussi par le titre de son module,
 * mais n'apparaît qu'une fois, dans les formations : la montrer en double, en
 * formation et en module, proposerait deux fois le même clic. Le groupe
 * « Modules » ne contient donc que les étapes des parcours, qui s'ouvrent sur
 * l'écran de leur parcours — l'accès à une étape s'y lit, verrous compris.
 *
 * ⚠️ Aucun accès n'est décidé ici : `unlocked` vient de l'API (ADR 0002 § 3).
 */
export function searchLearnerCatalog(sessions: readonly SessionView[], query: string): LearnerSearchResults {
  if (searchWords(query).length === 0) return { sessions: [], modules: [] }

  const sessionHits = sessions
    .filter(session => matchesQuery(query, [
      session.name,
      ...(isPath(session) ? [] : steps(session).map(step => step.module?.title)),
    ]))
    .slice(0, MAX_SESSIONS)
    .map((session) => {
      const single = isPath(session) ? undefined : steps(session)[0]
      return {
        session,
        status: completionStatus(session),
        progress: progressPct(session),
        moduleTitle: single?.module?.title ?? undefined,
        locked: single != null && single.unlocked !== true,
        target: sessionTarget(session),
      }
    })

  const moduleHits = sessions
    .filter(isPath)
    .flatMap(session => steps(session)
      .filter(step => matchesQuery(query, [step.module?.title]))
      .map(step => ({
        session,
        step,
        status: stepStatus(step),
        locked: step.unlocked !== true,
        target: `/my-sessions/${session.id}`,
      })))
    .slice(0, MAX_MODULES)

  return { sessions: sessionHits, modules: moduleHits }
}
